import { Fragment, h } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { useHistory } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { AppPage } from '../_common/components/app-page'
import { C, F } from '../_common/layout'
import { t } from '../_i18n'
import { ButtonLink, ButtonPrimary } from '../_common/components/buttons/buttons'
import Modal from '../_common/components/Modal/index'
import Loading from '../_common/components/loading/Loading'
import { Select } from '../_common/forms/components/components'
import { downloadFile } from '../utils'
import { isSpanishUser } from '../_common/utils/getLang'
import {
    createCertificateCart,
    fetchAcademicProgramCertificates,
    requestDigitalCertificate
} from '../_rx/certificates/actions'
import './secretary-styles.scss'

export default function RequestCertificate() {
    const history = useHistory()
    const dispatch = useDispatch()
    const enrolledAcademicPrograms = useSelector(state => state.status?.student_meta?.enrolled_academic_programs || [])
    const certificatesByAcademicProgram = useSelector(state => state.certificates?.academicProgramCertificates || {})
    const spanishUser = useSelector(state => isSpanishUser(state.status?.student_meta?.shop_id))
    const academicPrograms = useMemo(
        () => enrolledAcademicPrograms.filter(program => program.formative_zone),
        [enrolledAcademicPrograms]
    )

    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedCertificate, setSelectedCertificate] = useState(null)
    const [selectedFormat, setSelectedFormat] = useState('physical')
    const [lastMultiFormatSelection, setLastMultiFormatSelection] = useState('physical')
    const [modal, setModal] = useState(null)
    const [isCatalogLoading, setIsCatalogLoading] = useState(false)
    const [hasCatalogLoaded, setHasCatalogLoaded] = useState(false)
    const [isSubmittingPhysical, setIsSubmittingPhysical] = useState(false)
    const [isSubmittingDigital, setIsSubmittingDigital] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    const certificates = selectedCourse ? certificatesByAcademicProgram[selectedCourse] || [] : []
    const certificate = certificates.find(item => Number(item.id_product_attribute) === Number(selectedCertificate))
    const digital = certificate?.digital

    useEffect(() => {
        if (!academicPrograms.length) {
            setSelectedCourse(null)
            setSelectedCertificate(null)
            return
        }

        const hasSelectedCourse = academicPrograms.some(program => Number(program.id) === Number(selectedCourse))
        if (!hasSelectedCourse) setSelectedCourse(Number(academicPrograms[0].id))
    }, [academicPrograms, selectedCourse])

    useEffect(() => {
        if (!selectedCourse) return undefined

        let mounted = true

        const loadCertificates = async () => {
            setIsCatalogLoading(true)
            setHasCatalogLoaded(false)
            setSelectedCertificate(null)

            try {
                const response = await dispatch(fetchAcademicProgramCertificates(selectedCourse))
                if (mounted) {
                    setSelectedCertificate(Number(response?.[0]?.id_product_attribute) || null)
                    setSelectedFormat(response?.[0]?.digital ? 'digital' : 'physical')
                    if (response?.[0]?.digital) setLastMultiFormatSelection('digital')
                }
            } catch (error) {
                if (mounted) {
                    setSelectedCertificate(null)
                    setSelectedFormat('physical')
                }
            } finally {
                if (mounted) {
                    setIsCatalogLoading(false)
                    setHasCatalogLoaded(true)
                }
            }
        }

        loadCertificates()

        return () => {
            mounted = false
        }
    }, [dispatch, selectedCourse])

    const handleCourseSelect = event => {
        if (isSubmittingPhysical) return

        setHasCatalogLoaded(false)
        setSelectedCourse(Number(event.target.value))
    }

    const handleCertificateSelect = event => {
        if (isSubmittingPhysical) return

        const nextCertificate = certificates.find(item => Number(item.id_product_attribute) === Number(event.target.value))
        setSelectedCertificate(Number(event.target.value))
        setSelectedFormat(nextCertificate?.digital ? lastMultiFormatSelection : 'physical')
    }

    const handlePhysicalRequest = async () => {
        if (isSubmittingPhysical || !certificate) return

        setModal(null)
        setIsSubmittingPhysical(true)
        try {
            const url = await dispatch(createCertificateCart(selectedCourse, certificate.id_product_attribute))
            if (url) {
                window.open(url, '_self')
                return
            }

            setIsSubmittingPhysical(false)
        } catch (error) {
            // El error ya se comunica mediante globalCatch.
            setIsSubmittingPhysical(false)
        }
    }

    const handleDigitalRequest = async () => {
        if (isSubmittingPhysical || isSubmittingDigital || !certificate) return

        setModal(null)
        setIsSubmittingDigital(true)
        setIsCatalogLoading(true)
        try {
            await dispatch(requestDigitalCertificate(selectedCourse, certificate.id_product_attribute))
        } catch (error) {
            // El error ya se comunica mediante globalCatch.
        } finally {
            setIsCatalogLoading(false)
            setIsSubmittingDigital(false)
        }
    }

    const handleDownload = async () => {
        if (isSubmittingPhysical || isDownloading || !digital?.document_download_url) return

        setIsDownloading(true)
        try {
            await downloadFile({
                url: digital.document_download_url,
                fileName: `${certificate.name}.pdf`,
                credentials: 'include',
                debug: true
            })
        } finally {
            setIsDownloading(false)
        }
    }

    const renderDigitalAction = () => {
        if (digital?.document_download_url) {
            return <Fragment>
                <p className="mb-8 m-top">{t('¡Tu certificado ya está listo! Puedes descargarlo ahora en formato PDF. También te hemos enviado una copia a tu correo electrónico.')}</p>
                <ButtonPrimary
                    className="no-text-transform w-full whitespace-nowrap break-keep lg:w-auto"
                    disabled={isSubmittingPhysical || isDownloading}
                    onClick={handleDownload}
                >
                    {isDownloading ? t('Descargando en formato digital') : t('Descargar en formato digital')}
                </ButtonPrimary>
            </Fragment>
        }

        if (digital?.in_progress) {
            return <Fragment>
                <ButtonPrimary className="no-text-transform btn-inverse w-full whitespace-nowrap break-keep lg:w-auto" disabled>
                    {t('Solicitar en formato digital')}
                </ButtonPrimary>
                <p className="mb-0 m-top-x2 font-16"><strong>{t('Solicitud en proceso. ')}</strong>{t('Ya hemos recibido tu petición para este certificado. Te lo enviaremos por correo electrónico en un plazo de 5 a 7 días laborables. No es necesario que vuelvas a solicitarlo.')}</p>
            </Fragment>
        }

        if (digital?.can_request) {
            return <ButtonPrimary
                className="no-text-transform btn-inverse w-full whitespace-nowrap break-keep lg:w-auto"
                disabled={isSubmittingPhysical}
                onClick={() => setModal('digital')}
            >
                {t('Solicitar en formato digital')}
            </ButtonPrimary>
        }

        return null
    }

    return <AppPage title={t('Solicitar certificado')} back={isSubmittingPhysical ? null : history.goBack} className="max-width-980">
        <F className="max-width-980">
            <C>
                {!academicPrograms.length ? (
                    <p>{t('No tienes ciclos disponibles. Contacta con Secretaría Académica.')}</p>
                ) : !hasCatalogLoaded ? <Loading label={`${t('Cargando')}. . . `} /> : <Fragment>
                    <div className="max-width-580 mb-12">
                        {academicPrograms.length > 1 && <div className="m-bottom">
                            <span className="mb-2">{t('Ciclo:')}</span>
                            <div className="flex items-center gap-3">
                                <div className="min-w-0 flex-1">
                                    <Select
                                        disabled={isCatalogLoading || isSubmittingPhysical}
                                        noEmptyOption
                                        onChange={handleCourseSelect}
                                        options={academicPrograms.map(program => ({
                                            key: program.id,
                                            name: program.title
                                        }))}
                                        required
                                        value={selectedCourse}
                                    />
                                </div>
                                <span aria-hidden="true" className="h-6 w-6 shrink-0" />
                            </div>
                        </div>}
                        <div>
                            <span className="mb-2">{t('Certificado:')}</span>
                            <div className="flex items-center gap-3">
                                <div className="min-w-0 flex-1">
                                    <Select
                                        disabled={isCatalogLoading || isSubmittingPhysical || !certificates.length}
                                        noEmptyOption
                                        onChange={handleCertificateSelect}
                                        options={certificates.map(item => ({
                                            key: item.id_product_attribute,
                                            name: item.name
                                        }))}
                                        required
                                        value={selectedCertificate}
                                    />
                                </div>
                                {certificate?.image ? <button
                                    aria-label={t('Ver ejemplo de certificado')}
                                    className="h-6 w-6 shrink-0 border-0 bg-transparent p-0"
                                    disabled={isSubmittingPhysical}
                                    onClick={() => setModal('preview')}
                                    title={t('Ver ejemplo de certificado')}
                                    type="button"
                                >
                                    <i className="far fa-info-circle info-icon m-0" />
                                </button> : <span aria-hidden="true" className="h-6 w-6 shrink-0" />}
                            </div>
                        </div>
                    </div>

                    {isCatalogLoading ? <Loading label={`${t('Cargando')}. . . `} /> : certificate ? <Fragment>
                        {digital && <div className="flex border-b border-neutral-200 mb-8">
                            <button
                                aria-selected={selectedFormat === 'digital'}
                                className={`w-1/2 border-b-2 px-4 py-3 text-center ${selectedFormat === 'digital' ? 'border-primary font-bold' : 'border-transparent'}`}
                                disabled={isSubmittingPhysical}
                                onClick={() => {
                                    setSelectedFormat('digital')
                                    setLastMultiFormatSelection('digital')
                                }}
                                role="tab"
                                type="button"
                            >
                                {t('Formato digital (Gratuito)')}
                            </button>
                            <button
                                aria-selected={selectedFormat === 'physical'}
                                className={`w-1/2 border-b-2 px-4 py-3 text-center ${selectedFormat === 'physical' ? 'border-primary font-bold' : 'border-transparent'}`}
                                disabled={isSubmittingPhysical}
                                onClick={() => {
                                    setSelectedFormat('physical')
                                    setLastMultiFormatSelection('physical')
                                }}
                                role="tab"
                                type="button"
                            >
                                {t('Formato físico (Con coste)')}
                            </button>
                        </div>}

                        <div role="tabpanel">
                            {spanishUser && <p className="alert alert-info mb-6">
                                <strong>{t('Aviso para la Comunidad Valenciana y Galicia: ')}</strong>{t('Si solicitas un certificado de notas para la administración de estas comunidades, contacta con Secretaría Académica después de realizar el pago.')}
                            </p>}

                            {selectedFormat === 'physical' ?
                                <Fragment>
                                    <p className="mb-8">{t('Elige el certificado que necesitas y completa el pago. Te llegará a casa en un plazo de 5 a 7 días laborables. Recuerda que si solo necesitas ')} <strong> {t('el certificado en formato digital, puedes solicitarlo de forma gratuita ')}</strong> {t('desde la pestaña superior.')}</p>
                                    <ButtonPrimary
                                        className="no-text-transform btn-inverse w-full whitespace-nowrap break-keep lg:w-auto"
                                        disabled={isSubmittingPhysical}
                                        onClick={() => setModal('physical')}
                                    >
                                        {isSubmittingPhysical ? t('Solicitando en formato físico') : t('Solicitar en formato físico')}
                                    </ButtonPrimary>
                                </Fragment>
                              :
                              <Fragment>
                                  {!digital?.document_download_url && (
                                    <p className="mb-8">
                                        {t('La emisión en formato digital es ')}
                                        <strong>{t('gratuita. ')}</strong>
                                        {t('Te enviaremos el certificado a tu correo electrónico en un plazo de ')}
                                        <strong>{t('5 a 7 días laborables.')}</strong>
                                    </p>
                                  )}

                                  {renderDigitalAction()}
                              </Fragment>

                            }
                        </div>
                    </Fragment> : <p className="m-x2-top">{t('No hay certificados disponibles para este ciclo.')}</p>}
                </Fragment>}
            </C>
        </F>

        {modal === 'preview' && certificate?.image && <Modal visible closeModal={() => setModal(null)} bodyClass="mx-4 max-w-[800px] max-h-[95vh] overflow-auto">
            <div className="flex flex-col">
                <div className="flex items-center justify-between border-b border-neutral-200 px-8 py-6">
                    <p className="t-left font-18 no-margin">{certificate.name}</p>
                    <button className="modal-close-jobs modal-link" onClick={() => setModal(null)} type="button">
                        <i className="fal fa-times font-20" />
                    </button>
                </div>
                <div className="p-8">
                    <img alt={t('Ejemplo de certificado')} className="certificate-modal-img" src={certificate.image} />
                </div>
            </div>
        </Modal>}

        {modal === 'physical' && certificate && <Modal visible closeModal={() => setModal(null)} bodyClass="mx-4 max-w-[800px]">
            <div className="flex flex-col">
                <div className="flex items-center justify-between border-b border-neutral-200 px-8 py-6">
                    <p className="t-left font-24 line-height-24 no-margin font-bold">{t('Solicitar en formato físico')}</p>
                    <button className="modal-close-jobs modal-link" onClick={() => setModal(null)} type="button">
                        <i className="fal fa-times font-20" />
                    </button>
                </div>
                <div className="px-8 py-6 flex flex-col gap-4">
                    <p>{t('Estás a punto de solicitar tu ')} <strong>{certificate.name}</strong> {t('impreso.')}</p>
                    <p>
                        <p>{t('Tasa de expedición: ')} <strong>{t('19 €')}</strong></p>
                        <p>{t('Entrega: ')} <strong>{t('5 a 7 días laborables ')}</strong> {t('en tu domicilio.')}</p>
                    </p>
                    <p>{t('(Si prefieres la versión digital, es totalmente gratuita).')}</p>
                </div>
                <div className="flex flex-col gap-4 px-8 pb-6 sm:flex-row sm:justify-end">
                    <ButtonLink className="no-text-transform no-decoration txt-color-black w-full whitespace-nowrap break-keep font-bold lg:w-auto" onClick={() => setModal(null)}>
                        {t('Cancelar')}
                    </ButtonLink>
                    <ButtonPrimary
                        className="no-text-transform btn-inverse w-full whitespace-nowrap break-keep lg:w-auto"
                        disabled={isSubmittingPhysical}
                        onClick={handlePhysicalRequest}
                    >
                        {t('Solicitar')}
                    </ButtonPrimary>
                </div>
            </div>
        </Modal>}

        {modal === 'digital' && certificate && <Modal visible closeModal={() => setModal(null)} bodyClass="mx-4 max-w-[800px]">
            <div className="flex flex-col">
                <div className="flex items-center justify-between border-b border-neutral-200 px-8 py-6">
                    <p className="t-left font-24 line-height-24 no-margin font-bold">{t('Solicitar en formato digital gratis')}</p>
                    <button className="modal-close-jobs modal-link" onClick={() => setModal(null)} type="button">
                        <i className="fal fa-times font-20" />
                    </button>
                </div>
                <div className="px-8 py-6 flex flex-col gap-4">
                    <p>{t('¿Estás seguro de que quieres solicitar')} {certificate.name} {t('en formato digital?')}</p>
                    <p>{t('Lo recibirás en tu correo electrónico en un plazo de ')} <strong>{t('5 a 7 días laborables.')}</strong></p>
                </div>
                <div className="flex flex-col gap-4 px-8 pb-6 sm:flex-row sm:justify-end">
                    <ButtonLink className="no-text-transform no-decoration txt-color-black w-full whitespace-nowrap break-keep font-bold lg:w-auto" onClick={() => setModal(null)}>
                        {t('Cancelar')}
                    </ButtonLink>
                    <ButtonPrimary
                        className="no-text-transform btn-inverse w-full whitespace-nowrap break-keep lg:w-auto"
                        disabled={isSubmittingDigital}
                        onClick={handleDigitalRequest}
                    >
                        {t('Solicitar')}
                    </ButtonPrimary>
                </div>
            </div>
        </Modal>}
    </AppPage>
}
